'use client'

import { useRef, useState } from 'react'
import Link from 'next/link'
import posthog from 'posthog-js'
import { Button } from '@/components/ui/button'
import { ArrowRight, Play } from 'lucide-react'
import { useLocale, useTranslations } from 'next-intl'

/**
 * Narrated 90-second product tour, anchored at /#demo so emails and ads can
 * deep-link straight to it. Click-to-play (the video has voiceover — never
 * autoplay with sound), with the talk-to-team CTA in the same viewport.
 */
export function DemoVideoSection() {
  const t = useTranslations('landing')
  const locale = useLocale()
  const videoRef = useRef<HTMLVideoElement>(null)
  const [playing, setPlaying] = useState(false)
  const [videoError, setVideoError] = useState(false)

  // Localized cuts exist for es/fr (narration + on-screen text); everything
  // else gets the English master.
  const langSuffix = locale === 'es' || locale === 'fr' ? `.${locale}` : ''

  // PostHog issue 019f7132: an unhandled `DOMException: NotSupportedError`
  // from the native `play()` rejection (ad blocker, flaky network, or a
  // transient media-pipeline hiccup — not a codec issue on this asset) was
  // surfacing as an uncaught exception. Handle it: fall back to the
  // click-to-play overlay instead of leaving a broken, controls-less player.
  const handlePlaybackError = () => {
    setPlaying(false)
    setVideoError(true)
    posthog.capture('landing_demo_video_error', { video_locale: langSuffix ? locale : 'en' })
  }

  const handlePlay = () => {
    setPlaying(true)
    setVideoError(false)
    videoRef.current?.play().catch(handlePlaybackError)
    posthog.capture('landing_demo_video_play', { video_locale: langSuffix ? locale : 'en' })
  }

  return (
    <section id="demo" className="scroll-mt-16 bg-primary py-16 text-primary-foreground md:py-20">
      <div className="container mx-auto px-4">
        {/* Compact header so an email deep-link to /#demo lands with the video
            AND the talk-to-team CTA inside a 900px-tall viewport. */}
        <div className="mx-auto mb-8 max-w-3xl text-center">
          <p className="mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-primary-foreground/60">
            {t('demoVideo.eyebrow')}
          </p>
          <h2 className="font-serif text-3xl font-bold leading-[1.1] md:text-4xl">
            {t('demoVideo.headline')}{' '}
            <span className="italic text-white/85">{t('demoVideo.headlineAccent')}</span>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg leading-relaxed text-primary-foreground/80">
            {t('demoVideo.subhead')}
          </p>
        </div>

        <div className="mx-auto max-w-3xl">
          <div className="relative overflow-hidden rounded-2xl shadow-2xl ring-1 ring-white/15">
            <video
              ref={videoRef}
              className="block aspect-video w-full"
              src={`/promo/alumnia-demo-video${langSuffix}.mp4`}
              poster={`/promo/alumnia-demo-poster${langSuffix}.jpg`}
              preload="metadata"
              playsInline
              controls={playing}
              onEnded={() => posthog.capture('landing_demo_video_completed')}
              onError={handlePlaybackError}
            />
            {!playing && (
              <button
                type="button"
                onClick={handlePlay}
                aria-label={t('demoVideo.playAria')}
                className="group absolute inset-0 flex cursor-pointer flex-col items-center justify-center gap-4 bg-slate-950/30 transition-colors hover:bg-slate-950/20"
              >
                <span className="flex h-20 w-20 items-center justify-center rounded-full bg-white text-primary shadow-xl transition-transform group-hover:scale-110">
                  <Play className="ml-1 h-8 w-8" fill="currentColor" />
                </span>
                <span className="rounded-full bg-slate-950/60 px-4 py-1.5 text-sm font-medium text-white backdrop-blur-sm">
                  {videoError ? t('demoVideo.playError') : t('demoVideo.playHint')}
                </span>
              </button>
            )}
          </div>

          {/* Conversion path, in the same viewport as the video */}
          <div className="mt-6 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <a
              href="#lead-form"
              onClick={() =>
                posthog.capture('landing_demo_video_cta_click', { cta: 'talk_to_team' })
              }
            >
              <Button
                size="lg"
                className="h-12 rounded-full bg-white px-8 text-base font-semibold text-primary shadow-lg hover:bg-white/90"
              >
                {t('demoVideo.talkToTeam')} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </a>
            <p className="text-sm text-primary-foreground/70">
              {t('demoVideo.orPrefix')}{' '}
              <Link href="/start" className="underline underline-offset-4 hover:text-white">
                {t('demoVideo.startFree')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}
